import { forwardRef, ForwardRefRenderFunction, memo, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { FormHelperText } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';

import { dateFormat, dateFormatForDayJs } from '@shared/constants/formats';
import { DatePickerWrapper, InputLabel, TextInputWrapper } from '@styles/InputStyles';
import { useDebounce } from '@shared/hooks/useDebounce';
import { dateWithoutTimeZone } from '@shared/commonFunctions';

import { ITextInputOwnProps } from '../inputs/TextInput';

type TView = 'day' | 'month' | 'year';

export interface ISelectDateProps extends ITextInputOwnProps {
  id?: string;
  label?: string;
  name?: string;
  disablePast?: boolean;
  disableFuture?: boolean;
  views?: TView[];
  format?: string;
  value?: string | null;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  defaultValue?: string | null;
  error?: boolean;
  helperText?: ReactNode;
  required?: boolean;
  displayError?: boolean;
  disabled?: boolean;
  withoutTimeZone?: boolean;
  onChange?: (event: SelectDateEventType) => void;
  onAfterChange?: (value?: string) => void;
  onBlur?: (event: SelectDateEventType) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
}

export type SelectDateEventType = {
  target: {
    value: string | null;
    name?: string;
  };
};

const SelectDateBase: ForwardRefRenderFunction<HTMLDivElement, ISelectDateProps> = ({
  name,
  label,
  views,
  disablePast,
  disableFuture,
  format = dateFormat,
  value,
  error,
  helperText,
  minDate,
  maxDate,
  required,
  displayError,
  disabled,
  onChange,
  defaultValue,
  onAfterChange,
  onBlur,
  onKeyDown,
  ...props
}) => {
  const [selectedDateInput, setInputValue] = useState<string>();
  const [isDatePickerFocused, setIsDatePickerFocused] = useState(false);
  const debouncedInputValue = useDebounce(selectedDateInput, 1000);

  const valueIsMissing = !value && value !== '';
  const errorRequired = displayError && required && valueIsMissing;
  const errorRequiredText = errorRequired && `${label} is required`;

  const [isOpenend, setIsOpenend] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(
    () => {
      onAfterChange?.(debouncedInputValue);
    },
    [debouncedInputValue]
  );

  const handleChange = useCallback(
    (inputValue: Dayjs | null) => {
      const newDate = dayjs(inputValue).format(dateFormatForDayJs);
      setInputValue(inputValue ? newDate : '');
      if (!onChange) return;
      onChange({ target: { value: inputValue ? newDate : '', name } });
    },
    [onChange]
  );

  const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = dayjs(event.target.value, dateFormat).format(dateFormatForDayJs);
    onBlur && onBlur({ target: { value: inputValue, name } });
    setIsDatePickerFocused(false);
    setIsOpenend(false);
  }, [onBlur]);

  const handleDaySelect = useCallback(
    (inputValue: unknown) => {
      handleChange(inputValue as Dayjs);
      setIsDatePickerFocused(false);
      const newDate = dayjs(inputValue as Dayjs).format(dateFormatForDayJs);
      onBlur && onBlur({ target: { value: inputValue ? newDate : '', name } });
      setIsOpenend(false);
    }, [handleChange, onBlur]);

  const handlePopoverBlur = useCallback((e: React.FocusEvent<HTMLDivElement, Element>) => {
    let isChild = false;
    if (e.relatedTarget && pickerRef.current) {
      isChild = pickerRef.current.contains(e.relatedTarget);
    }
    !isChild && setIsOpenend(false);
  }, []);

  return (
    <DatePickerWrapper
      error={error}
      isFocused={isDatePickerFocused}
      onFocus={() => setIsDatePickerFocused(true)}
    >
      <TextInputWrapper withLabel={!!label}>
        {label && <InputLabel>{label}</InputLabel>}

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DemoContainer components={['DatePicker']}>
            <DatePicker
              value={value ? dayjs(dateWithoutTimeZone(value ?? '')) : null}
              minDate={minDate && dayjs(minDate)}
              maxDate={maxDate && dayjs(maxDate)}
              disabled={disabled}
              format={format}
              disableFuture={disableFuture}
              disablePast={disablePast}
              views={views}
              defaultValue={dayjs(dateWithoutTimeZone(defaultValue ?? ''))}
              open={isOpenend}
              slotProps={{
                openPickerButton: {
                  onClick: () => setIsOpenend(!isOpenend)
                },
                textField: {
                  onBlur: handleBlur,
                  onKeyDown,
                },
                popper: {
                  ref: pickerRef,
                  onBlur: handlePopoverBlur,
                },
                day: {
                  onDaySelect: handleDaySelect,
                }
              }
              }
              onChange={handleChange}
              {...props}
            />
          </DemoContainer>
        </LocalizationProvider>

        {(helperText || errorRequiredText) && (
          <FormHelperText error={error}>{helperText || errorRequiredText}</FormHelperText>
        )}
      </TextInputWrapper>
    </DatePickerWrapper>
  );
};

export const SelectDate = memo(forwardRef(SelectDateBase));
